import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import MarketplaceEngagement from "@/components/MarketplaceEngagement";
import MarketplaceSmsSenders from "@/components/MarketplaceSmsSenders";
import LeadQualityReview from "@/components/LeadQualityReview";

const fieldClass = "mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm";
const buttonClass = "rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50";
const INITIAL = {
  draft_id: "", seller_legal_name: "Lazaro Ariel Fortun", privacy_policy_url: "", disclosure_version: "",
  disclosure_text: "", buyer_names: "", buyer_sharing_text: "",
  sms_consent_text: "", call_consent_text: "", review_reference: "",
  compliance_review_attested: false
};

export default function LeadMarketplace() {
  const [drafts, setDrafts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [captureEnabled, setCaptureEnabled] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const set = (key, value) => setForm(current => ({...current, [key]: value}));

  const refresh = async () => {
    const [draftResult, campaignResult, leadResult, capabilityResult] = await Promise.all([
      api.get("/platform/campaign-director/drafts"),
      api.get("/platform/lead-marketplace/campaigns"),
      api.get("/platform/lead-marketplace/leads"),
      api.get("/platform/lead-marketplace/capabilities").catch(() => ({data:{capture_enabled:false}})),
    ]);
    const ds = draftResult.data || [];
    setDrafts(ds);
    setCampaigns(campaignResult.data || []);
    setLeads(leadResult.data || []);
    setCaptureEnabled(capabilityResult.data?.capture_enabled === true);
    setForm(old => ({...old, draft_id: old.draft_id || ds[0]?.id || ""}));
  };
  useEffect(() => {
    let active = true;
    refresh().catch(e => {
      if(active) setError(formatApiErrorDetail(e?.response?.data?.detail) || "Unable to load the lead marketplace.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const create = async e => {
    e.preventDefault();
    setError(""); setNotice("");
    if (!form.compliance_review_attested) { setError("Compliance review must be completed first."); return; }
    const buyers = form.buyer_names.split(",").map(s => s.trim()).filter(Boolean);
    setBusy(true);
    try {
      await api.post("/platform/lead-marketplace/campaigns", {
        ...form, buyer_names: buyers,
      });
      setNotice("Marketplace campaign created as a draft. It is not yet publicly active.");
      setForm(INITIAL);
      await refresh();
    } catch(e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Could not create the campaign.");
    } finally { setBusy(false); }
  };
  const changeStatus = async (campaign, next) => {
    setError(""); setNotice("");
    if(next === "activate" && !window.confirm(
      "Activate public consumer intake with the disclosures you approved? Confirm that the named legal entity, buyer disclosures and privacy policy have been reviewed. This does not publish paid ads or start calls."
    )) return;
    setBusy(true);
    try {
      await api.post(`/platform/lead-marketplace/campaigns/${campaign.id}/${next}`);
      setNotice(next === "activate" ? "Public intake activated." : "Public intake paused.");
      await refresh();
    } catch(e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Action could not be completed.");
    } finally { setBusy(false); }
  };
  const suppress = async lead => {
    if(!window.confirm("Suppress this contact from marketplace inventory and future marketing?")) return;
    setBusy(true);
    try {
      await api.post(`/platform/lead-marketplace/leads/${lead.id}/suppress`);
      setNotice("Contact suppressed.");
      await refresh();
    } catch(e) { setError(formatApiErrorDetail(e?.response?.data?.detail) || "Unable to suppress contact."); }
    finally { setBusy(false); }
  };
  const input = (key, label, type = "text") => (
    <label className="block text-sm">{label}
      <input required={["seller_legal_name","privacy_policy_url","disclosure_version","review_reference"].includes(key)}
        type={type} className={fieldClass} value={form[key]} onChange={e => set(key, e.target.value)}/>
    </label>
  );
  const textarea = (key, label, required = false) => (
    <label className="block text-sm">{label}
      <textarea required={required} rows={4} className={fieldClass} value={form[key]}
        onChange={e => set(key, e.target.value)} />
    </label>
  );
  if (loading) return <div className="p-6 text-sm">Loading Lead Marketplace…</div>;
  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="marketplace-owner-page">
      <header>
        <h1 className="font-heading text-2xl font-bold">Fortunate Operations Lead Marketplace</h1>
        <p className="text-sm text-muted-foreground">First-party Final Expense inquiries owned by Fortunate Operations. Activation and consumer permissions are required before accepting live submissions.</p>
      </header>
      {notice && <p role="status" className="rounded-md border p-3 text-sm">{notice}</p>}
      {error && <p role="alert" className="rounded-md border p-3 text-sm text-destructive">{error}</p>}
      <section className="space-y-3 rounded-xl border bg-card p-5">
        <h2 className="text-lg font-semibold">Campaigns and public intake</h2>
        {campaigns.length === 0 && <p className="text-sm text-muted-foreground">No live-marketplace campaigns configured yet.</p>}
        {campaigns.map(c => (
          <div key={c.id} className="space-y-2 rounded-md border p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <strong>{c.campaign_name || "Final Expense campaign"}</strong>
              <span className="rounded-full border px-2 text-xs">{c.status}</span>
            </div>
            <div className="text-xs text-muted-foreground">{c.seller_legal_name} · Consent {c.disclosure_version} · {c.buyer_names?.length || 0} disclosed buyers</div>
            {c.status === "active" && <a className="block break-all text-sm underline"
              href={`/final-expense/${c.public_token}`} target="_blank" rel="noreferrer">
              {window.location.origin}/final-expense/{c.public_token}
            </a>}
            <div className="flex gap-2">
              {c.status !== "active" && (captureEnabled
                ? <button className={buttonClass} disabled={busy}
                    onClick={() => changeStatus(c, "activate")}>Activate public intake</button>
                : <span className="text-xs text-muted-foreground" data-testid="intake-not-ready">
                    Public intake is disabled in deployment settings; this campaign cannot be activated yet.
                  </span>)}
              {c.status === "active" && <button className={buttonClass} disabled={busy}
                onClick={() => changeStatus(c, "pause")}>Pause intake</button>}
            </div>
          </div>
        ))}
      </section>
      <MarketplaceSmsSenders />
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={create} className="space-y-4 rounded-xl border bg-card p-5">
          <h2 className="text-lg font-semibold">Create reviewed acquisition campaign</h2>
          <p className="text-xs text-muted-foreground">Before using this for lead resale, confirm that the linked privacy policy actually permits the proposed data sharing. The existing Fortunate Operations privacy page says mobile information is not sold for third-party marketing.</p>
          <p className="text-xs text-muted-foreground">This uses an existing Campaign Director draft. No advertising is launched from this screen. A legal/compliance reviewer must approve the exact consumer disclosures before activation.</p>
          <label className="block text-sm">Campaign Director draft
            <select required className={fieldClass} value={form.draft_id} onChange={e=>set("draft_id",e.target.value)}>
              <option value="">Select a saved draft</option>
              {drafts.map(d => <option key={d.id} value={d.id}>{d.proposal?.name || d.id}</option>)}
            </select>
          </label>
          {input("seller_legal_name", "Legal operator's full name (sole proprietor)")}
          <p className="text-xs text-muted-foreground">The sole proprietor is Lazaro Ariel Fortun. Do not identify the operator as an LLC or a registered DBA unless that legal status is established. If the operator changes, update this field and obtain a new compliance review.</p>
          {input("privacy_policy_url", "Published HTTPS privacy policy URL", "url")}
          {input("disclosure_version", "Approved disclosure version")}
          {textarea("disclosure_text", "Legally reviewed general privacy and request disclosure", true)}
          {input("buyer_names", "Named eligible buyer entities (comma separated, optional)")}
          {textarea("buyer_sharing_text", "Reviewed optional sharing consent naming every listed buyer")}
          {textarea("sms_consent_text", "Reviewed optional SMS consent (leave blank to disable SMS opt-in)")}
          {textarea("call_consent_text", "Reviewed optional calls / AI voice consent (leave blank to disable call opt-in)")}
          {input("review_reference", "Legal or compliance review reference")}
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.compliance_review_attested} onChange={e => set("compliance_review_attested", e.target.checked)}/>
            I attest the exact entity identity, privacy policy, disclosures and named-buyer sharing language were reviewed for the intended campaign.
          </label>
          <button className={buttonClass} disabled={!drafts.length || busy || !form.compliance_review_attested} type="submit">
            {busy ? "Saving…" : "Create controlled campaign"}
          </button>
        </form>
        <section className="rounded-xl border bg-card p-5">
          <h2 className="text-lg font-semibold">Fortunate Operations lead inventory</h2>
          <p className="mb-4 text-xs text-muted-foreground">Private to the platform owner. No buyer receives these records automatically. A sharing opt-in is not proof of the buyer's license or prepaid order.</p>
          {!leads.length && <p className="text-sm text-muted-foreground">No inquiries received yet.</p>}
          <div className="max-h-[700px] space-y-2 overflow-auto">
            {leads.map(lead => (
              <div key={lead.id} className="rounded-md border p-3 text-sm">
                <strong>{lead.client_name}</strong>
                <div>{lead.email} · {lead.phone}</div>
                <div className="text-xs text-muted-foreground">
                  {lead.status} · {lead.sale_eligibility} · {lead.consent?.sharing_allowed ? "Buyer sharing requested" : "No buyer-sharing consent"}
                </div>
                <LeadQualityReview lead={lead} onReviewed={refresh} />
                <MarketplaceEngagement lead={lead} campaign={campaigns.find(c=>c.id===lead.campaign_id)} />
                {lead.status !== "suppressed" && <button className={buttonClass + " mt-2"} disabled={busy}
                  onClick={() => suppress(lead)}>Suppress contact</button>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
